import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import { getAllServices, createService, updateService, deleteService } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Car,
  Clock,
  DollarSign,
  Tag,
  MapPin,
  Home,
  Truck
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Autos', 'Motos', 'Camioneta', 'Todo Terreno', 'Servicios Extra'
];

const SERVICE_TYPES = [
  { value: 'centro_lavado', label: 'Centro de Lavado', icon: Home },
  { value: 'domicilio', label: 'Servicio a Domicilio', icon: MapPin },
  { value: 'recogida_entrega', label: 'Recogida y Entrega', icon: Truck }
];

const SERVICE_TYPE_LABELS = {
  centro_lavado: 'Centro de Lavado',
  domicilio: 'Domicilio',
  recogida_entrega: 'Recogida y Entrega'
};

export default function AdminServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    duration: '',
    description: '',
    category: '',
    subcategory: '',
    service_types: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await getAllServices();
      setServices(res.data);
    } catch (error) {
      toast.error('Error al cargar servicios');
    }
    setLoading(false);
  };

  const handleOpenModal = (service = null) => {
    if (service) {
      setEditingService(service);
      setFormData({
        name: service.name,
        price: service.price.toString(),
        duration: service.duration.toString(),
        description: service.description || '',
        category: service.category || '',
        subcategory: service.subcategory || '',
        service_types: service.service_types || []
      });
    } else {
      setEditingService(null);
      setFormData({
        name: '',
        price: '',
        duration: '',
        description: '',
        category: '',
        subcategory: '',
        service_types: []
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    const data = {
      name: formData.name,
      price: parseFloat(formData.price),
      duration: parseInt(formData.duration),
      description: formData.description || null,
      category: formData.category || null,
      subcategory: formData.subcategory || null,
      service_types: formData.service_types
    };
    
    try {
      if (editingService) {
        await updateService(editingService.id, data);
        toast.success('Servicio actualizado');
      } else {
        await createService(data);
        toast.success('Servicio creado');
      }
      setShowModal(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
    setSubmitting(false);
  };

  const handleToggleActive = async (service) => {
    try {
      await updateService(service.id, { active: !service.active });
      setServices(services.map(s => 
        s.id === service.id ? { ...s, active: !s.active } : s
      ));
      toast.success(service.active ? 'Servicio desactivado' : 'Servicio activado');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (service) => {
    if (!window.confirm(`¿Eliminar "${service.name}"?`)) return;
    
    try {
      await deleteService(service.id);
      setServices(services.filter(s => s.id !== service.id));
      toast.success('Servicio eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Servicios</h1>
            <p className="text-slate-600 mt-1">Administra los servicios del car wash</p>
          </div>
          <Button onClick={() => handleOpenModal()} className="gap-2" data-testid="add-service-btn">
            <Plus className="w-5 h-5" />
            Nuevo Servicio
          </Button>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2" data-testid="admin-service-filter">
          {['all', ...CATEGORIES].map(cat => (
            <button
              key={cat}
              className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                filterCategory === cat
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              onClick={() => setFilterCategory(cat)}
              data-testid={`admin-filter-${cat}`}
            >
              {cat === 'all' ? 'Todos' : cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {services
            .filter(s => filterCategory === 'all' || s.category === filterCategory)
            .map((service) => (
            <div 
              key={service.id}
              className={`card-base p-6 ${!service.active ? 'opacity-60' : ''}`}
              data-testid={`service-card-${service.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Car className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{service.name}</h3>
                    <div className="text-sm text-slate-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {service.duration} min
                      {service.category && (
                        <>
                          <span className="mx-1">·</span>
                          <Tag className="w-3 h-3" />
                          <span>{service.category}{service.subcategory ? ` / ${service.subcategory}` : ''}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-xl font-bold text-blue-600">
                  {formatCurrency(service.price)}
                </div>
              </div>
              
              {service.description && (
                <p className="text-sm text-slate-600 mb-2">{service.description}</p>
              )}

              {service.service_types && service.service_types.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4" data-testid={`service-types-${service.id}`}>
                  {service.service_types.map(type => (
                    <span key={type} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                      {type === 'centro_lavado' && <Home className="w-3 h-3" />}
                      {type === 'domicilio' && <MapPin className="w-3 h-3" />}
                      {type === 'recogida_entrega' && <Truck className="w-3 h-3" />}
                      {SERVICE_TYPE_LABELS[type] || type}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={service.active}
                    onCheckedChange={() => handleToggleActive(service)}
                    data-testid={`service-toggle-${service.id}`}
                  />
                  <span className="text-sm text-slate-600">
                    {service.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleOpenModal(service)}
                    data-testid={`edit-service-${service.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-700"
                    onClick={() => handleDelete(service)}
                    data-testid={`delete-service-${service.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingService ? 'Editar Servicio' : 'Nuevo Servicio'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Lavado Premium"
                required
                className="mt-1"
                data-testid="service-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price">Precio (MXN) *</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="price"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="150"
                    required
                    className="pl-10"
                    data-testid="service-price-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="duration">Duración (min) *</Label>
                <div className="relative mt-1">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="duration"
                    type="number"
                    min="1"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="30"
                    required
                    className="pl-10"
                    data-testid="service-duration-input"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descripción del servicio..."
                className="mt-1"
                data-testid="service-description-input"
              />
            </div>

            {/* Category & Subcategory */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoría</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v })}
                >
                  <SelectTrigger className="mt-1" data-testid="service-category-select">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="subcategory">Subcategoría</Label>
                <Input
                  id="subcategory"
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  placeholder="Ej: Didi, Auto, Cross Over"
                  className="mt-1"
                  data-testid="service-subcategory-input"
                />
              </div>
            </div>

            {/* Tipo de Servicio */}
            <div>
              <Label>Tipo de Servicio</Label>
              <div className="mt-2 space-y-2" data-testid="service-types-checkboxes">
                {SERVICE_TYPES.map(({ value, label, icon: Icon }) => (
                  <label
                    key={value}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`service-type-${value}`}
                  >
                    <Checkbox
                      checked={formData.service_types.includes(value)}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({
                          ...prev,
                          service_types: checked
                            ? [...prev.service_types, value]
                            : prev.service_types.filter(t => t !== value)
                        }));
                      }}
                    />
                    <Icon className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full"
              disabled={submitting}
              data-testid="save-service-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Guardar'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
